import React, { useState, useEffect } from 'react';
import './PhoneFrame.css';
import PropertyCarousel from '../PropertyCarousel/PropertyCarousel';
import RoomSelection from '../RoomSelection/RoomSelection';
import Calendar from '../Calendar/Calendar';
import FiltersView from '../FiltersView/FiltersView';
import RoomDetails from '../RoomDetails/RoomDetails';
import PriceBreakdownView from '../RoomDetails/PriceBreakdownView';
import ConfirmationView from '../ConfirmationView/ConfirmationView';
import FinalBookingView from '../FinalBookingView/FinalBookingView';
import { apiService } from '../../services/api';
import { format, addDays, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const PhoneFrame = ({ 
  currentPropertyIndex, 
  setCurrentPropertyIndex, 
  properties,
  currentScreen,
  setCurrentScreen,
  checkinDate,
  setCheckinDate,
  checkoutDate,
  setCheckoutDate,
  guests,
  setGuests,
  onBookNow,
  selectedRoom,
  onConfirm,
  onBack,
  onDone,
  finalBookingData
}) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedRoomLocal, setSelectedRoomLocal] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [screenBeforeCalendar, setScreenBeforeCalendar] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const d = new Date(checkinDate);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // State for filters
  const [filters, setFilters] = useState({
    sortBy: 'price_asc',
    rateTypes: [],
    roomTypes: [],
    bedTypes: [],
    accessibleOnly: false,
  });
  const [filterOptions, setFilterOptions] = useState({
    rateTypes: [],
    roomTypes: [],
    bedTypes: [],
  });
  const [filteredRoomCount, setFilteredRoomCount] = useState(0);

  // State for all rooms data
  const [rawRoomsData, setRawRoomsData] = useState([]);
  const [processedRooms, setProcessedRooms] = useState([]);

  // New state for selected rate
  const [selectedRateId, setSelectedRateId] = useState(null);

  const [priceBreakdownTitle, setPriceBreakdownTitle] = useState('Price Breakdown');
  const [currentRateForBreakdown, setCurrentRateForBreakdown] = useState(null);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);

  const getBreakdownForRate = (rate, room) => {
    if (!rate || !room) return null;

    return {
      roomName: room.room.name,
      rateName: rate.name,
      rateDescription: rate.description,
      nightly: rate.nightly_rates?.map(night => ({
        date: night.date,
        price: night.amount_before_tax
      })) || [],
      taxesAndFees: rate.taxes?.map(tax => ({
        name: tax.description,
        details: tax.details || '',
        amount: tax.amount
      })) || [],
      totalPrice: rate.total_after_tax || 0,
      policies: rate.policies || []
    };
  };

  const handleShowPriceBreakdown = (rate, title, roomForBreakdown) => {
    const rateWithBreakdown = { ...rate, breakdown: getBreakdownForRate(rate, roomForBreakdown) };
    setCurrentRateForBreakdown(rateWithBreakdown);
    setPriceBreakdownTitle(title || 'Price Breakdown');
    setShowPriceBreakdown(true);
  };

  const handleClosePriceBreakdown = () => {
    setShowPriceBreakdown(false);
    setTimeout(() => {
      setCurrentRateForBreakdown(null);
    }, 300);
  };

  // Get the current propertyId for the calendar API
  const currentProperty = properties && properties.length > 0 ? properties[currentPropertyIndex] : null;
  const propertyId = currentProperty ? currentProperty.id : null;

  // Fetch calendar data when modal opens or visibleMonth/propertyId changes
  useEffect(() => {
    if (showCalendar && propertyId && visibleMonth) {
      const year = visibleMonth.getFullYear();
      const month = visibleMonth.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`;
      setCalendarLoading(true);
      setCalendarError(null);
      apiService.getCalendarData(propertyId, startDate, endDate, guests)
        .then(data => {
          setCalendarData(data);
          setCalendarLoading(false);
        })
        .catch(err => {
          setCalendarError('Failed to load calendar data');
          setCalendarLoading(false);
        });
    }
  }, [showCalendar, propertyId, visibleMonth, guests]);

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    const propertyIndex = properties.findIndex(p => p.id === property.id);
    if (propertyIndex !== -1) {
      setCurrentPropertyIndex(propertyIndex);
    }
    setCurrentScreen('rooms');
  };

  const handleRoomSelect = (room) => {
    setSelectedRoomLocal(room);
    setCurrentScreen('roomDetails');
  };

  const handleBackToProperties = () => {
    setCurrentScreen('properties');
    setSelectedProperty(null);
  };

  const handleBackToRooms = () => {
    setCurrentScreen('rooms');
    setSelectedRoomLocal(null);
  };

  // Helper to open calendar and remember previous screen
  const handleOpenCalendar = () => {
    setScreenBeforeCalendar(currentScreen);
    setCurrentScreen('calendar');
    setShowCalendar(true);
  };

  // Calendar close logic: when dates are selected, restore previous screen
  const handleCalendarDateChange = (dates) => {
    console.log('Calendar date change:', dates);
    if (dates.length === 2) {
      const [start, end] = dates;
      setCheckinDate(start);
      setCheckoutDate(end);
      // Ensure we have a property selected for room search
      if (!selectedProperty) {
        setSelectedProperty(properties[currentPropertyIndex]);
      }
      // After selecting dates, go to the room selection screen
      setCurrentScreen('rooms');
      setShowCalendar(false);
      setScreenBeforeCalendar(null);
    }
  };

  const handleCalendarClose = () => {
    setShowCalendar(false);
    setCurrentScreen('properties');
    setScreenBeforeCalendar(null);
  };

  // Month navigation handlers
  const handlePrevMonth = () => {
    setVisibleMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    });
  };
  const handleNextMonth = () => {
    setVisibleMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    });
  };

  // When opening the calendar, reset visibleMonth to checkinDate's month
  useEffect(() => {
    if (showCalendar) {
      const d = new Date(checkinDate);
      setVisibleMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [showCalendar, checkinDate]);

  return (
    <div className="phone-frame">
      <div className="phone-screen">
        <div className="phone-notch"></div>
        <div className="app-container">
          {currentScreen === 'properties' && (
            <PropertyCarousel
              currentPropertyIndex={currentPropertyIndex}
              setCurrentPropertyIndex={setCurrentPropertyIndex}
              onPropertySelect={handlePropertySelect}
              properties={properties}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              onShowCalendar={handleOpenCalendar}
            />
          )}
          {currentScreen === 'rooms' && (
            <RoomSelection
              selectedProperty={selectedProperty || properties[currentPropertyIndex]}
              onBack={handleBackToProperties}
              checkInDate={checkinDate}
              checkOutDate={checkoutDate}
              onShowFilters={() => setShowFilters(true)}
              filters={filters}
              onFiltersChange={setFilters}
              setFilterOptions={setFilterOptions}
              setFilteredRoomCount={setFilteredRoomCount}
              onRoomSelect={handleRoomSelect}
              guests={guests}
              rawRoomsData={rawRoomsData}
              setRawRoomsData={setRawRoomsData}
              processedRooms={processedRooms}
              setProcessedRooms={setProcessedRooms}
            />
          )}
          {currentScreen === 'roomDetails' && (
            <RoomDetails 
              room={selectedRoomLocal}
              allRoomsData={rawRoomsData}
              onClose={handleBackToRooms}
              onShowPriceBreakdown={(rate, title) => handleShowPriceBreakdown(rate, title, selectedRoomLocal)}
              checkInDate={checkinDate}
              checkOutDate={checkoutDate}
              guests={guests}
              onBookNow={(room) => onBookNow(room)}
              selectedRateId={selectedRateId}
              setSelectedRateId={setSelectedRateId}
              onUpgrade={handleRoomSelect}
            />
          )}
          {currentScreen === 'confirmation' && (
            <ConfirmationView
              selectedRoom={selectedRoom}
              onConfirm={onConfirm}
              onBack={onBack}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              guests={guests}
            />
          )}
          {currentScreen === 'final' && (
            <FinalBookingView
              selectedRoom={finalBookingData.selectedRoom}
              onDone={onDone}
              checkinDate={finalBookingData.checkinDate}
              checkoutDate={finalBookingData.checkoutDate}
              guests={finalBookingData.guests}
              property={finalBookingData.property}
            />
          )}
        </div>
        
        {showPriceBreakdown && (
          <PriceBreakdownView 
            breakdown={currentRateForBreakdown?.breakdown} 
            onClose={handleClosePriceBreakdown}
            title={priceBreakdownTitle}
            isModal={true}
          />
        )}

        {showCalendar && (
          <div className="calendar-modal-overlay">
            <div className="calendar-modal">
              <Calendar
                calendarData={calendarData}
                checkinDate={checkinDate}
                checkoutDate={checkoutDate}
                setCheckinDate={setCheckinDate}
                setCheckoutDate={setCheckoutDate}
                onDone={handleCalendarClose}
                onMonthChange={{ prev: handlePrevMonth, next: handleNextMonth }}
                visibleMonth={visibleMonth}
                loading={calendarLoading}
                error={calendarError}
                onSelectDates={handleCalendarDateChange}
                guests={guests}
                setGuests={setGuests}
              />
              {calendarLoading && <div className="calendar-loading-spinner">Loading...</div>}
              {calendarError && <div className="calendar-error">{calendarError}</div>}
            </div>
          </div>
        )}
        <FiltersView
          show={showFilters}
          onClose={() => setShowFilters(false)}
          filters={filters}
          onFiltersChange={setFilters}
          filterOptions={filterOptions}
          filteredRoomCount={filteredRoomCount}
        />
      </div>
    </div>
  );
};

export default PhoneFrame;